import { useState } from 'react';
import { Alert, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { UserGroup } from '@nkr/enums/user-group';
import { IOpoPortalUser, IOpoUserAddRequest, IOpoUserUpdateRequest } from '@nkr/models/opo-portal-users';
import useOpoPortalUsers from '@nkr/store/opo/hooks/use-opo-portal-users';
import { SettingsPageWrapper } from '../styles';
import { Pill, SettingsTableCard } from '../table-styles';
import ConfirmModal from '../confirm-modal';
import { AddUserModal, EditUserModal, roleOptions } from './user-modals';

const roleName = (groupId: number, groupName: string) => roleOptions.find(x => x.value == groupId)?.label || groupName || '';

const renderStatus = (user: IOpoPortalUser) => {
    if (user.isDeactivated) {
        return <Pill $color='gray'>Deactivated</Pill>;
    }

    if (user.isVerificationExpired) {
        return <Pill $color='amber'>Verification expired</Pill>;
    }

    if (user.isPendingVerification) {
        return <span title={user.verificationExpiresDate ? `Link expires ${user.verificationExpiresDate}` : undefined}><Pill $color='amber'>Pending verification</Pill></span>;
    }

    return <Pill $color='green'>Active</Pill>;
};

const PortalUsers = () => {
    const { data, loading, error, isEmailAvailable, addUser, resendVerification, updateUser, deactivateUser } = useOpoPortalUsers();
    const [showAdd, setShowAdd] = useState(false);
    const [editing, setEditing] = useState<IOpoPortalUser | null>(null);
    const [deactivating, setDeactivating] = useState<IOpoPortalUser | null>(null);
    const [actionError, setActionError] = useState('');

    if (loading) {
        return null;
    }

    const canManage = data?.canManage == true;

    const handleAdd = async (request: IOpoUserAddRequest) => {
        const result = await addUser(request);

        if (result.success) {
            setShowAdd(false);
            toast.success(`User added. A verification email was sent to ${request.email}`);
        }

        return result;
    };

    const handleSave = async (userId: number, request: IOpoUserUpdateRequest) => {
        const result = await updateUser(userId, request);

        if (result.success) {
            setEditing(null);
            toast.success('User saved');
        }

        return result;
    };

    const handleResult = (message: string) => (result: { success: boolean, message: string }) => {
        if (result.success) {
            toast.success(message);
        } else {
            setActionError(result.message);
        }
    };

    const handleResend = (user: IOpoPortalUser) => {
        setActionError('');
        resendVerification(user.id).then(handleResult(`Verification email sent to ${user.email}`));
    };

    const handleDeactivate = () => {
        const user = deactivating;

        setActionError('');
        setDeactivating(null);
        deactivateUser(user.id).then(handleResult(`${user.firstName} ${user.lastName} has been deactivated`));
    };

    return (
        <SettingsPageWrapper style={{ maxWidth: 1000 }}>
            {(error || actionError) && (
                <Alert bsStyle='danger' onDismiss={actionError ? () => setActionError('') : undefined}>{error || actionError}</Alert>
            )}

            {data && !canManage && (
                <div className='settings-notice'>Only OPO administrators can add or manage portal users.</div>
            )}

            {data && (
                <SettingsTableCard>
                    <div className='table-card-header'>
                        <h4>Portal Users</h4>
                        <span className='count'>{data.users.length}</span>
                        {canManage && (
                            <button type='button' className='btn btn-primary' onClick={() => setShowAdd(true)}>Add User</button>
                        )}
                    </div>

                    <div className='table-card-body'>
                        <Table condensed>
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Username</th>
                                    <th className='role'>Role</th>
                                    <th>Status</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.users.map(user => {
                                    const isSelf = user.id == data.currentUserId;

                                    return (
                                        <tr key={user.id}>
                                            <td>{user.firstName} {user.lastName}{isSelf && <span className='muted'> (you)</span>}</td>
                                            <td>{user.email}</td>
                                            <td>{user.username}</td>
                                            <td><Pill $color={user.groupId == UserGroup.OpoAdmin ? 'blue' : 'gray'}>{roleName(user.groupId, user.groupName)}</Pill></td>
                                            <td>{renderStatus(user)}</td>
                                            <td>
                                                {canManage && user.isManageable && (
                                                    <div className='btn-group'>
                                                        {user.isPendingVerification && (
                                                            <button type='button' className='btn btn-default' onClick={() => handleResend(user)}>Resend Verification</button>
                                                        )}
                                                        <button type='button' className='btn btn-default' onClick={() => setEditing(user)}>Edit</button>
                                                        {!isSelf && (
                                                            <button type='button' className='btn btn-default' onClick={() => setDeactivating(user)}>Deactivate</button>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}

                                {data.users.length == 0 && (
                                    <tr>
                                        <td colSpan={6}>There are no portal users yet</td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </SettingsTableCard>
            )}

            <AddUserModal show={showAdd} positions={data?.positions || []} isEmailAvailable={isEmailAvailable} onAdd={handleAdd} onCancel={() => setShowAdd(false)} />

            <EditUserModal
                user={editing}
                positions={data?.positions || []}
                isSelf={editing != null && data != null && editing.id == data.currentUserId}
                onSave={handleSave}
                onCancel={() => setEditing(null)}
            />

            <ConfirmModal
                show={deactivating != null}
                title='Deactivate User'
                message={<>Deactivate <strong>{deactivating?.firstName} {deactivating?.lastName}</strong>? They will no longer be able to sign in to the OPO portal{deactivating?.isPendingVerification ? ', and their verification link will stop working' : ''}.</>}
                confirmText='Deactivate'
                onConfirm={handleDeactivate}
                onCancel={() => setDeactivating(null)}
            />
        </SettingsPageWrapper>
    );
}

export default PortalUsers;
